import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'

interface Post {
  profile_url: string
  profile_name?: string
  name?: string
  posts_fetched?: number
  posts_sample?: string[]
  top_post_summary?: string
  matched_keywords?: string[]
  explanation?: string
  relevance_score?: number
  provider_id?: string
}

const columnHelper = createColumnHelper<Post>()

const columns = [
  columnHelper.accessor((row) => row.profile_name || row.name || '-', {
    id: 'profile_name',
    header: 'Author',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('top_post_summary', {
    header: 'Summary',
    cell: (info) => (
      <span className="text-sm text-slate-300 max-w-md truncate block" title={info.getValue() || ''}>
        {info.getValue() || '-'}
      </span>
    ),
  }),
  columnHelper.accessor('matched_keywords', {
    header: 'Keywords',
    cell: (info) => (
      <span className="text-sm text-slate-400 max-w-xs truncate block">
        {(info.getValue() || []).join(', ') || '-'}
      </span>
    ),
  }),
  columnHelper.accessor('posts_fetched', {
    header: 'Posts',
    cell: (info) => <span className="font-mono text-xs">{info.getValue() ?? 0}</span>,
  }),
  columnHelper.accessor('relevance_score', {
    header: 'Relevance',
    cell: (info) => {
      const score = info.getValue()
      if (score == null) return <span className="text-xs text-slate-500">-</span>
      const percentage = score <= 1 ? Math.round(score * 100) : Math.round(score)
      const color = percentage >= 70 ? 'text-green-400' : percentage >= 50 ? 'text-yellow-400' : 'text-red-400'
      return (
        <span className={`text-xs font-mono font-medium ${color}`} title={info.row.original.explanation || ''}>
          {percentage}%
        </span>
      )
    },
  }),
  columnHelper.accessor('profile_url', {
    header: 'Profile',
    cell: (info) => (
      <a
        href={String(info.getValue()).startsWith('http') ? info.getValue() : `https://${info.getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
      >
        <ExternalLink className="w-3 h-3" />
        View
      </a>
    ),
  }),
]

interface PostsTableProps {
  posts: Post[]
}

export function PostsTable({ posts }: PostsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'relevance_score', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data: posts,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <input
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-md text-sm"
          placeholder="Search posts..."
        />
        <span className="text-xs text-slate-500">{posts.length} posts</span>
      </div>

      <div className="border border-slate-800 rounded-md overflow-hidden">
        <div className="max-w-full overflow-auto max-h-96">
          <table className="min-w-[820px] w-full text-sm">
            <thead className="bg-slate-900 sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-normal cursor-pointer hover:bg-slate-800"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-800">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-900">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
